import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogOut, MessageSquare, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ChatUI from "@/components/ChatUI";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "gg_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null); // thread object
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const authHeader = useCallback(
    () => ({ headers: { "X-Admin-Token": token } }),
    [token]
  );

  const loadThreads = useCallback(async () => {
    if (!token) return;
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/threads`, authHeader());
      setThreads(res.data.threads || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setToken("");
        localStorage.removeItem(TOKEN_KEY);
      }
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }, [token, authHeader]);

  // Initial + interval refresh of thread list
  useEffect(() => {
    if (!token) return;
    loadThreads();
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, [token, loadThreads]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoggingIn(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSelected(null);
    setThreads([]);
  };

  const fetchMessages = async (after) => {
    const url = `${API}/admin/threads/${selected.id}/messages${
      after ? `?after=${encodeURIComponent(after)}` : ""
    }`;
    const res = await axios.get(url, authHeader());
    return res.data;
  };

  const sendMessage = async ({ text, attachments }) => {
    const res = await axios.post(
      `${API}/admin/threads/${selected.id}/messages`,
      { text, attachments },
      authHeader()
    );
    // After sending, refresh threads list to update preview
    loadThreads();
    return res.data;
  };

  // -------- LOGIN VIEW --------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <div className="max-w-sm w-full">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3 flex items-center gap-2">
            <Lock className="h-3 w-3" /> Admin
          </div>
          <h1 className="font-display text-5xl leading-[0.9]" data-testid="admin-title">
            COMMAND <span className="text-yellow">CENTER</span>.
          </h1>
          <p className="text-[#a1a1aa] mt-4 text-sm">
            Enter the admin password to access buyer chats.
          </p>

          <form onSubmit={handleLogin} className="mt-8 space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              data-testid="admin-password-input"
              className="w-full bg-[#0a0a0a] border border-[#262626] focus:border-yellow text-white px-4 py-3 outline-none font-mono"
            />
            {error && (
              <div className="text-red-400 text-sm font-mono" data-testid="admin-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loggingIn || !password}
              className="btn-brutal w-full justify-center disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loggingIn ? "Verifying…" : "Enter"}
            </button>
          </form>

          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-[#71717a] hover:text-yellow font-mono text-xs uppercase tracking-[0.2em]"
          >
            <ArrowLeft className="h-3 w-3" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  // -------- DASHBOARD --------
  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1a1a1a] px-4 sm:px-6 py-3 flex items-center justify-between bg-[#050505] sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[#a1a1aa] hover:text-yellow transition">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <span className="font-display text-lg tracking-wide uppercase">
            ADMIN · <span className="text-yellow">INBOX</span>
          </span>
          {refreshing && (
            <RefreshCw className="h-3 w-3 text-yellow animate-spin" />
          )}
        </div>
        <button
          onClick={logout}
          className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717a] hover:text-yellow flex items-center gap-2"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-3 w-3" /> Logout
        </button>
      </header>

      {/* Two-pane layout */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Threads list */}
        <aside
          className="lg:w-80 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-[#1a1a1a] overflow-y-auto max-h-[40vh] lg:max-h-none"
          data-testid="admin-threads-list"
        >
          <div className="px-4 py-3 border-b border-[#1a1a1a] font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] flex items-center justify-between">
            <span>Buyer Threads · {threads.length}</span>
            <button
              onClick={loadThreads}
              className="text-yellow hover:text-white"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className="h-3 w-3" />
            </button>
          </div>
          {threads.length === 0 && (
            <div className="px-4 py-10 text-center text-[#71717a]">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-50" />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em]">
                No chats yet
              </div>
            </div>
          )}
          {threads.map((t) => {
            const isActive = selected?.id === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setSelected(t)}
                data-testid={`admin-thread-item-${t.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#1a1a1a] transition ${
                  isActive
                    ? "bg-[#111111] border-l-2 border-l-yellow"
                    : "hover:bg-[#0a0a0a]"
                }`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-display text-lg tracking-wide truncate">
                    {t.buyer_name}
                  </span>
                  {t.unread_admin > 0 && (
                    <span className="bg-yellow text-black text-[10px] font-mono px-1.5 py-0.5 leading-none">
                      {t.unread_admin}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[#a1a1aa] truncate mt-1">
                  {t.last_message_preview || "—"}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#52525b] mt-1">
                  {formatRelative(t.last_message_at)}
                </div>
              </button>
            );
          })}
        </aside>

        {/* Chat panel */}
        <main className="flex-1 min-h-0 p-3 lg:p-6">
          {!selected && (
            <div className="h-full flex items-center justify-center text-center text-[#71717a]">
              <div>
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <div className="font-display text-2xl uppercase">Pick a thread</div>
                <div className="font-mono text-xs uppercase tracking-[0.2em] mt-1">
                  Select a buyer on the left to start chatting
                </div>
              </div>
            </div>
          )}
          {selected && (
            <div className="h-[60vh] lg:h-full flex">
              <ChatUI
                threadId={selected.id}
                currentSide="admin"
                title={selected.buyer_name}
                subtitle={`Session: ${selected.session_id.slice(0, 24)}…`}
                fetchMessages={fetchMessages}
                sendMessage={sendMessage}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

export default Admin;
